import { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, Dumbbell, CreditCard, Calendar, CheckSquare, DollarSign, Settings, LogOut, UserCog, Building2, Activity, Menu, X } from "lucide-react";
import { getUser, logout, hasPermission, isAdmin, isSuperuser } from "@/utils/auth";
import { Button } from "@/components/ui/button";

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => window.innerWidth >= 1024);

  const menuItems = user?.role === 'member' 
    ? [
        { 
          icon: LayoutDashboard, 
          label: "Dashboard", 
          path: "/member/dashboard", 
          permission: "dashboard" 
        },
        { 
          icon: Calendar, 
          label: "My Subscriptions", 
          path: "/member/subscriptions", 
          permission: "dashboard" 
        },
        { 
          icon: CheckSquare, 
          label: "My Attendance", 
          path: "/member/attendance", 
          permission: "dashboard" 
        },
        { 
          icon: DollarSign, 
          label: "My Payments", 
          path: "/member/payments", 
          permission: "dashboard" 
        },
        { 
          icon: Activity, 
          label: "My Health", 
          path: "/member/health", 
          permission: "dashboard" 
        }
      ]
    : [
        { 
          icon: LayoutDashboard, 
          label: "Dashboard", 
          path: "/", 
          permission: "dashboard" 
        },
        { icon: Users, label: "Members", path: "/members", permission: "members" },
        { icon: Dumbbell, label: "Trainers", path: "/trainers", permission: "trainers" },
        { icon: CreditCard, label: "Plans", path: "/membership-plans", permission: "plans" },
        { icon: Calendar, label: "Subscriptions", path: "/subscriptions", permission: "subscriptions" },
        { icon: CheckSquare, label: "Attendance", path: "/attendance", permission: "attendance" },
        { icon: DollarSign, label: "Ledger", path: "/financial-ledger", permission: "payments" },
        { icon: Settings, label: "Settings", path: "/settings", permission: "settings" },
      ];

  const filteredMenuItems = menuItems.filter(item => hasPermission(item.permission));

  return (
    <div className="flex h-screen overflow-hidden bg-zinc-50">
      
      {/* Header - Always visible */}
      <div className="fixed top-0 left-0 right-0 h-16 bg-zinc-900 border-b border-zinc-800 z-[60] flex items-center justify-between px-4 shadow-md">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-2 text-zinc-900 bg-lime-400 rounded-md hover:bg-lime-500 transition-colors border border-lime-500 shadow-sm"
            aria-label={isSidebarOpen ? "Close menu" : "Open menu"}
          >
            {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <div className="flex items-center gap-3">
            <img src="/logo192.png" alt="IronPulse" className="h-8 w-8 object-contain" />
            <h1 className="text-xl font-black text-lime-400" style={{ fontFamily: 'Chivo, sans-serif' }}>
              IronPulse
            </h1>
            {(() => {
              const companyName = user?.company?.name || user?.tenant?.name;
              const logoUrl = user?.company?.logo_url || user?.tenant?.logo_url;
              const fullLogoUrl = logoUrl ? `${process.env.REACT_APP_BACKEND_URL}${logoUrl}` : null;

              return companyName ? (
                <div className="flex items-center gap-3">
                  <div className="h-4 w-px bg-zinc-700"></div>
                  {fullLogoUrl && (
                    <img 
                      src={fullLogoUrl} 
                      alt={companyName} 
                      className="h-8 w-8 object-contain bg-white rounded-sm p-0.5"
                    />
                  )}
                  <span className="text-sm text-zinc-400 font-medium truncate max-w-[150px] sm:max-w-md">
                    {companyName}
                  </span>
                </div>
              ) : null;
            })()}
          </div>
        </div>
      </div>

      {/* Overlay - Mobile only */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed left-0 bottom-0 z-50 w-64 bg-zinc-900 text-white flex flex-col transform transition-transform duration-300 ease-in-out top-16 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 border-b border-zinc-800 hidden lg:block">
          <h1 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            <span className="text-lime-400">Iron</span>Pulse
          </h1>
          {(() => {
            const companyName = user?.company?.name || user?.tenant?.name;
            return companyName ? (
              <p className="text-xs text-zinc-500 mt-2 truncate" title={companyName}>
                {companyName}
              </p>
            ) : null;
          })()}
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {filteredMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  if (window.innerWidth < 1024) setIsSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive
                    ? "bg-lime-400 text-zinc-900 font-bold"
                    : "text-zinc-400 hover:bg-zinc-800 hover:text-white"
                }`}
              >
                <Icon size={20} />
                <span>{item.label}</span>
              </button>
            );
          })}

          {hasPermission('settings') && (
            <>
              <button
                onClick={() => {
                  navigate("/company-settings");
                  if (window.innerWidth < 1024) setIsSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  location.pathname === "/company-settings"
                    ? "bg-lime-400 text-zinc-900 font-bold"
                    : "text-zinc-400 hover:bg-zinc-800 hover:text-white"
                }`}
              >
                <Building2 size={20} />
                <span>Company</span>
              </button>
              <button
                onClick={() => {
                  navigate("/users");
                  if (window.innerWidth < 1024) setIsSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  location.pathname === "/users"
                    ? "bg-lime-400 text-zinc-900 font-bold"
                    : "text-zinc-400 hover:bg-zinc-800 hover:text-white"
                }`}
              >
                <UserCog size={20} />
                <span>Users</span>
              </button>
            </>
          )}
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-zinc-800">
          <div className="mb-3 p-3 bg-zinc-800 rounded-lg">
            <p className="text-sm font-bold text-white truncate">{user?.full_name}</p>
            <p className="text-xs text-zinc-400 truncate">{user?.email}</p>
            <p className="text-xs text-lime-400 mt-1 uppercase">{user?.role}</p>
          </div>
          <Button
            onClick={logout}
            variant="outline"
            className="w-full bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white"
          >
            <LogOut size={16} className="mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 overflow-y-auto pt-16 transition-all duration-300 ${isSidebarOpen ? 'lg:ml-64' : 'lg:ml-0'}`}>
        <div className="p-4 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
